/**
 * Property Schema Registry
 * Phase 1: Foundation & Type Safety
 * 
 * Central registry for managing node property schemas
 */

import { NodePropertyDefinition, NodePropertySchema } from '../types/node-properties';

class PropertySchemaRegistry {
  private schemas: Map<string, NodePropertySchema> = new Map();

  /**
   * Register a property schema for a node type
   */
  registerSchema(schema: NodePropertySchema): void {
    this.schemas.set(schema.nodeType, schema);
  }

  /**
   * Register multiple schemas at once
   */
  registerSchemas(schemas: NodePropertySchema[]): void {
    schemas.forEach(schema => this.registerSchema(schema));
  }

  /**
   * Get property schema for a node type
   */
  getSchema(nodeType: string): NodePropertySchema | undefined {
    return this.schemas.get(nodeType);
  }

  /**
   * Get property definitions for a node type
   */
  getPropertyDefinitions(nodeType: string): NodePropertyDefinition[] {
    const schema = this.schemas.get(nodeType);
    return schema?.properties || [];
  }

  /**
   * Get a specific property definition
   */
  getPropertyDefinition(nodeType: string, propertyKey: string): NodePropertyDefinition | undefined {
    const definitions = this.getPropertyDefinitions(nodeType);
    return definitions.find(def => def.key === propertyKey);
  }

  /**
   * Check if a node type has property schema
   */
  hasSchema(nodeType: string): boolean {
    return this.schemas.has(nodeType);
  }

  /**
   * Get all registered node types
   */
  getRegisteredNodeTypes(): string[] {
    return Array.from(this.schemas.keys());
  }

  /**
   * Clear all schemas
   */
  clear(): void {
    this.schemas.clear();
  }

  /**
   * Remove a specific schema
   */
  removeSchema(nodeType: string): boolean {
    return this.schemas.delete(nodeType);
  }

  /**
   * Get all schemas
   */
  getAllSchemas(): NodePropertySchema[] {
    return Array.from(this.schemas.values());
  }
}

// Export singleton instance
export const propertySchemaRegistry = new PropertySchemaRegistry();

// Export class for testing
export { PropertySchemaRegistry };
